/**
 * Dry foods that are not TCS food (owner, 2026-10-10: "Dark chocolate covered almonds" got "Back in the fridge by 11:14 pm, or
 * throw it out." — "Probably this doesn't need to be refrigerated"). setupscan foodIsTcs asks this first, so the food store, the
 * label, the place suggestion and the left-out library agree.
 *
 * The rule, from the sources saved in docs/source/research/non-tcs-sources.txt and food-code-2022-excerpts.txt:
 *  - Food Code 2022 1-201.10(B)(3)(c): not TCS is "A FOOD that because of its PH or AW value ... is designated as a non-TCS FOOD
 *    in Table A or B". Table B: aw "< 0.88" is non-TCS at every pH (Table A: "≤0.92").
 *  - Food Code 8-401.10(B)(3) names "FOOD that is not TIME/TEMPERATURE CONTROL FOR SAFETY FOOD such as carbonated BEVERAGES and
 *    snack FOOD such as chips, nuts, popcorn, and pretzels."
 *  - FDA, low-moisture RTE foods guidance (Jan 2025): "Low-moisture food: A food that has a water activity of 0.85 or below", and
 *    its examples "peanut butter, nut butters, … chocolate, … processed tree nuts, … snack foods such as chips and crackers,
 *    granola bars, and dry cereal".
 *  - IFT report for FDA (2001), Table 3-1: honey 0.75, jam 0.75 to 0.80, dried fruit 0.55 to 0.80, cereal 0.10 to 0.20, sugar
 *    0.19, crackers 0.10; §8.4: "Traditional syrups do not need time/temperature control for safety because of high sugar
 *    content and low aw" (maple syrup 0.85).
 * DERIVED: each food below is under 0.88, so Table B makes it non-TCS at any pH.
 *
 * Strict on purpose: EVERY word of the name must be one of ours, and at least one must name the dry food. One word we don't know
 * ("mousse", "strawberries", "milk", "cream", "sauce", "free") and the food is not called dry here; the caller asks instead.
 * So "Chocolate mousse", "Chocolate covered strawberries", "Chocolate milk", "Sugar-free syrup" and "Simple syrup" (homemade,
 * 1 to 1, is not a traditional syrup) are never called shelf-stable by this list. Jellies are left out on purpose: Table 3-1
 * gives them 0.82 to 0.94, over 0.88 at the top.
 */

export type DryGroup = "chocolate" | "nuts" | "snack" | "cereal" | "dried_fruit" | "honey" | "jam" | "syrup" | "sugar";

export interface DryCall { groups: DryGroup[]; reason: string; citation: string }

const FC = "FDA Food Code 2022 1-201.10(B)(3)(c), Table B";
const SNACK = "FDA Food Code 2022 8-401.10(B)(3)";
const LMRTE = "FDA low-moisture foods guidance (2025): water activity 0.85 or below";
const IFT = "IFT/FDA report (2001), Table 3-1";
const IFT_SYRUP = "IFT/FDA report (2001) §8.4";

/** Each group: its words in the plain sentence, and the sources it rests on. */
const GROUP: Record<DryGroup, { words: string; cites: string[] }> = {
  chocolate: { words: "chocolate", cites: [LMRTE, FC] },
  nuts: { words: "nuts", cites: [SNACK, LMRTE, FC] },
  snack: { words: "chips, crackers, popcorn and pretzels", cites: [SNACK, LMRTE, FC] },
  cereal: { words: "granola and dry cereal", cites: [LMRTE, IFT, FC] },
  dried_fruit: { words: "dried fruit", cites: [IFT, FC] },
  honey: { words: "honey", cites: [IFT, FC] },
  jam: { words: "jam", cites: [IFT, FC] },
  syrup: { words: "maple or corn syrup", cites: [IFT_SYRUP, IFT, FC] },
  sugar: { words: "sugar", cites: [IFT, FC] },
};

/** Words, space-separated; a trailing "+" adds the plural with "s" too ("almond+" = almond, almonds). Nothing else is stemmed. */
const list = (w: string) => w.split(" ").flatMap((x) => (x.endsWith("+") ? [x.slice(0, -1), `${x.slice(0, -1)}s`] : [x]));
const NUTS = "nut+ almond+ cashew+ pecan+ walnut+ pistachio+ hazelnut+ macadamia+ peanut+";

/** Words that name the dry food (a "head"), by group. */
const HEADS: Record<string, DryGroup> = {};
const head = (g: DryGroup, w: string) => { for (const x of list(w)) HEADS[x] = g; };
head("chocolate", "chocolate+ milk_chocolate");
head("nuts", `${NUTS} trail_mix nut_butter peanut_butter almond_butter cashew_butter`);
head("snack", "chips cracker+ popcorn kettle_corn pretzel+");
head("cereal", "granola cereal+");
head("dried_fruit", "raisin+ craisins prunes dried_fruit");
head("honey", "honey");
head("jam", "jam+ preserves");
head("syrup", "maple_syrup corn_syrup");
head("sugar", "sugar brown_sugar powdered_sugar");

/** Fresh-fruit words: only with "dried" (or in a jam). Never alone: "Chocolate covered strawberries" is not a dry food. */
const FRUIT = new Set(list("fruit+ apricot+ mango+ mangoes cranberry cranberries cherry cherries fig+ dates apple+ banana+ blueberry blueberries strawberry strawberries pineapple peach peaches raspberry raspberries plum+ orange grape+ berry berries"));
const DRIED = new Set(["dried", "dehydrated", "freeze_dried"]);

/** Words that only describe (allowed next to a head, never enough alone). */
const MODIFIERS = new Set(list(`dark white semisweet semi sweet bittersweet covered coated dipped drizzled chip chunk+ pieces square+ bar+ salted unsalted lightly sea salt
roasted dry toasted raw smoked spiced candied glazed cinnamon vanilla caramel honey sliced slivered chopped crushed whole mixed mix assorted deluxe organic plain
original classic mini+ bite size potato tortilla pita corn kettle bbq barbecue vinegar and with of in the a bag+ box boxes jar+ pack+ packet+ bulk snack+ oat+
${NUTS} granulated pure grade amber graham house homemade made unopened sealed store bought commercial`.replace(/\s+/g, " ")));

/** Two-word names folded into one token before the words are read. */
const PHRASES: [RegExp, string | ((m: string) => string)][] = [
  [/\bmilk chocolate\b/g, "milk_chocolate"], [/\btrail mix\b/g, "trail_mix"], [/\bkettle corn\b/g, "kettle_corn"], [/\bfreeze dried\b/g, "freeze_dried"],
  [/\bdried fruits?\b/g, "dried_fruit"], [/\bmaple syrup\b/g, "maple_syrup"], [/\bcorn syrup\b/g, "corn_syrup"], [/\bbrown sugar\b/g, "brown_sugar"],
  [/\b(powdered|confectioners) sugar\b/g, "powdered_sugar"], [/\b(peanut|almond|cashew|nut) butter\b/g, (m) => m.replace(" ", "_")],
];

function tokens(label: string): string[] {
  let n = label.toLowerCase().replace(/&/g, " and ").replace(/[’']/g, "").replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
  for (const [re, to] of PHRASES) n = typeof to === "string" ? n.replace(re, to) : n.replace(re, to);
  return n ? n.split(" ") : [];
}

/**
 * The dry-food call for a name, or undefined when any word is one we don't know (then it's not ours to say). Pure.
 * "Dark chocolate covered almonds" → chocolate + nuts; "Chocolate mousse" → undefined.
 */
export function dryFoodCall(label: string): DryCall | undefined {
  const ws = tokens(label);
  if (!ws.length) return undefined;
  const dried = ws.some((w) => DRIED.has(w));
  const groups: DryGroup[] = [];
  const add = (g: DryGroup) => { if (!groups.includes(g)) groups.push(g); };
  let fruit = false;
  for (const w of ws) {
    if (DRIED.has(w)) continue;
    if (FRUIT.has(w)) { fruit = true; continue; }
    const g = HEADS[w];
    if (g) { add(g); continue; }
    if (!MODIFIERS.has(w)) return undefined;
  }
  // A fruit word is a dry food only when it is dried (or the jam's flavor); otherwise the name may be fresh fruit.
  if (fruit) {
    if (dried) add("dried_fruit");
    else if (!groups.includes("jam")) return undefined;
  } else if (dried && !groups.length) return undefined;
  // "Nut" words are heads and modifiers both; a head is needed.
  if (!groups.length) return undefined;
  // Made here: granola, nuts, chocolate bark, yes. Jam or syrup made here: its sugar (so its water activity) is unknown.
  if (ws.some((w) => w === "house" || w === "homemade" || w === "made") && groups.some((g) => g === "jam" || g === "syrup")) return undefined;
  const names = groups.map((g) => GROUP[g].words);
  const list = names.length === 1 ? names[0]! : `${names.slice(0, -1).join(", ")} and ${names.at(-1)!}`;
  const cites = [...new Set(groups.flatMap((g) => GROUP[g].cites))];
  // Facts only: the sources' own threshold and the Code's own table, in plain words.
  return { groups, reason: `${list.charAt(0).toUpperCase()}${list.slice(1)}: low water activity (under 0.88). The Food Code's Table B says food like that is not TCS food, so it doesn't need the fridge for safety.`, citation: cites.join("; ") };
}

/** Words that make a sentence about a food more than the dry food it names: wet, filled, fresh, or an animal food. */
const NOT_DRY = new Set(list(`milk cream custard mousse pudding cake cheesecake frosting frosted ganache filled filling sauce dip salsa queso cheese dough batter
smoothie yogurt fresh fish chicken beef pork meat egg+ ham turkey salad sandwich jelly jellies free simple light lite reduced`.replace(/\s+/g, " ")));

/**
 * For a sentence that names a dry food ("I left the chocolate almonds out overnight"): false when it also names something that
 * makes it not ours to call dry ("chocolate mousse", "chocolate covered strawberries", "fish and chips"). The left-out reader
 * uses it before landing a question on a dry item.
 */
export function dryQuestionOk(text: string): boolean {
  const ws = tokens(text);
  const dried = ws.some((w) => DRIED.has(w));
  const jam = ws.some((w) => HEADS[w] === "jam");
  return !ws.some((w) => NOT_DRY.has(w) || (FRUIT.has(w) && !dried && !jam));
}
